import { useQuery } from '@tanstack/react-query'
import FormGroup from 'common/components/core/FormGroup'
import Input from 'common/components/core/Input'
import InputLabel from 'common/components/core/InputLabel'
import {
  SurveyAnswer,
  SurveyQuestionType,
} from 'common/types/entities/SurveyInterface'
import React from 'react'
import { StylesConfig } from 'react-select'
import { getTagsResources } from 'src/editor/api/resources-api'
import { CheckboxNew } from 'src/editor/shared/ui-lib/checkbox-new'
import {
  OptionSingleValueType,
  OptionType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { getResourceBaseNameOptions } from 'src/editor/shared/utils/resources-utils'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { inputAnswerSize, inputOpenAnswerSize } from './constants/constants'
import {
  AnswerGroupTitleUi,
  AnswerGroupUi,
  AnswerInputGroupUi,
  AnswerNumberUi,
  AnswerRemoveUi,
} from './ui'

type QuestionProps = {
  question: SurveyQuestionType
  update: (question: SurveyQuestionType) => void
}

const colorPickerSelectModeStyles: StylesConfig<
  OptionType<number | null>,
  boolean
> = {
  valueContainer: inheritedStyles => ({
    ...inheritedStyles,
    padding: '7px 8px 8px 8px',
  }),
}

export const Question = ({ question, update }: QuestionProps) => {
  const { t } = useTypedTranslation()

  const updateQuestionTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    update({
      ...question,
      title: e.target.value,
    })
  }

  const { data: tagsOptions = [], isLoading: areTagsLoading } = useQuery({
    queryKey: ['tagsResources'],
    queryFn: () => getTagsResources(),
    select: getResourceBaseNameOptions,
  })

  const userTagsOptions: OptionType<number | null>[] = [
    {
      label: t('entity_settings.common.select.no_tag_option'),
      value: null,
    },
    ...(tagsOptions as OptionType<number>[]),
  ]

  const getSelectedOption = (answer: SurveyAnswer) => {
    if (answer.tagId) {
      const tag = userTagsOptions.find(item => item.value === answer.tagId)
      if (tag) {
        return {
          value: tag.value,
          label: tag.label,
        }
      } else {
        return {
          value: null,
          label: t(
            'entity_settings.survey.question_edit.answers_select.no_tag',
          ),
        }
      }
    } else {
      return {
        value: null,
        label: t('entity_settings.survey.question_edit.answers_select.no_tag'),
      }
    }
  }

  const updateQuestionSubTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    update({
      ...question,
      subTitle: e.target.value,
    })
  }

  const updateAnswerTitle = (index: number) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      update({
        ...question,
        answers: [
          ...question.answers.slice(0, index),
          { ...question.answers[index], title: e.target.value },
          ...question.answers.slice(index + 1),
        ],
      })
    }
  }

  const updateOpenAnswer = (index: number) => {
    return (value: boolean) => {
      update({
        ...question,
        answers: question.answers.map((answer, indexAnswer) => {
          if (index === indexAnswer && value) {
            return {
              id: answer.id,
              title: answer.title,
              isOpen: value,
            }
          } else {
            return {
              id: answer.id,
              title: answer.title,
            }
          }
        }),
      })
    }
  }

  const updateAnswerTag = (index: number) => {
    return (data: OptionSingleValueType<number | null>) => {
      if (!data) {
        return
      }
      if (data.value !== null) {
        update({
          ...question,
          answers: [
            ...question.answers.slice(0, index),
            { ...question.answers[index], tagId: data.value },
            ...question.answers.slice(index + 1),
          ],
        })
      } else {
        update({
          ...question,
          answers: [
            ...question.answers.slice(0, index),
            {
              id: question.answers[index].id,
              title: question.answers[index].title,
            },
            ...question.answers.slice(index + 1),
          ],
        })
      }
    }
  }

  const removeAnswer = (index: number) => {
    return () => {
      update({
        ...question,
        answers: [
          ...question.answers.slice(0, index),
          ...question.answers.slice(index + 1),
        ],
      })
    }
  }

  return (
    <React.Fragment>
      <FormGroup>
        <InputLabel>
          {t('entity_settings.survey.question_edit.title.label')}
        </InputLabel>
        <Input
          value={question.title}
          onChange={updateQuestionTitle}
          placeholder={t(
            'entity_settings.survey.question_edit.title_placeholder',
          )}
        />
      </FormGroup>
      <FormGroup>
        <InputLabel>
          {t('entity_settings.survey.question_edit.sub_title.label')}
        </InputLabel>
        <Input
          value={question.subTitle}
          onChange={updateQuestionSubTitle}
          placeholder={t(
            'entity_settings.survey.question_edit.sub_title_placeholder',
          )}
        />
      </FormGroup>
      <AnswerGroupTitleUi>
        <InputLabel>
          {t('entity_settings.survey.question_edit.answers_header')}
        </InputLabel>
        <InputLabel>
          {t('entity_settings.survey.question_edit.answers_header_tags')}
        </InputLabel>
      </AnswerGroupTitleUi>
      {question.answers.map((answer, index) => (
        <AnswerGroupUi key={answer.id}>
          <AnswerNumberUi>{index + 1}.</AnswerNumberUi>
          <AnswerInputGroupUi
            flexBasis={answer.isOpen ? inputOpenAnswerSize : inputAnswerSize}
          >
            <CheckboxNew
              wrapperClassName="mb-2"
              label={'entity_settings.survey.question_edit.open_answer'}
              checked={!!answer.isOpen}
              onChange={updateOpenAnswer(index)}
            />
            <Input
              value={answer.title}
              onChange={updateAnswerTitle(index)}
              placeholder={t(
                'entity_settings.survey.question_edit.answer_placeholder',
              )}
            />
          </AnswerInputGroupUi>
          {!answer.isOpen && (
            <SelectNew
              selectedOption={getSelectedOption(answer)}
              onChange={updateAnswerTag(index)}
              styles={colorPickerSelectModeStyles}
              itemStyles={{
                flexBasis: '30%',
                marginBottom: '0',
                padding: `${
                  index !== 0 ? '0px 0px 0px 15px' : '0px 5px 0px 15px'
                }`,
              }}
              options={userTagsOptions}
              isLoading={areTagsLoading}
              isMulti={false}
            />
          )}
          {index !== 0 && (
            <AnswerRemoveUi
              className="far fa-trash-alt"
              onClick={removeAnswer(index)}
            />
          )}
        </AnswerGroupUi>
      ))}
    </React.Fragment>
  )
}
